import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Truck,
  Download,
  LogOut,
  Search,
  RefreshCcw,
  MoreVertical,
  Trash2,
  CheckCircle2,
  Phone,
  Inbox,
  TrendingUp,
  ExternalLink,
  Users,
  DollarSign,
  Send,
  Globe,
  Pencil,
  Check,
  X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";

const STATUS_COLORS = {
  new: "bg-movewave-blue text-white",
  contacted: "bg-movewave-orange text-white",
  sold: "bg-green-600 text-white",
  archived: "bg-gray-400 text-white",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceInput, setPriceInput] = useState("");

  // bootstrap session
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await Promise.all([refreshLeads(), refreshStats(), refreshPartners()]);
      } catch (e) {
        navigate("/admin");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshLeads = async (params = {}) => {
    const { data } = await api.get("/admin/leads", { params });
    setLeads(data.leads || []);
  };

  const refreshStats = async () => {
    const { data } = await api.get("/admin/stats");
    setStats(data);
  };

  const refreshPartners = async () => {
    try {
      const { data } = await api.get("/admin/partners");
      setPartners(data.partners || []);
    } catch {}
  };

  const filtered = useMemo(() => {
    let l = leads;
    if (statusFilter !== "all") l = l.filter((x) => x.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      l = l.filter((x) =>
        [x.full_name, x.email, x.phone, x.from_zip, x.to_zip]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }
    return l;
  }, [leads, statusFilter, search]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((arr) => arr.map((l) => (l.id === id ? { ...l, status } : l)));
      if (selected?.id === id) setSelected({ ...selected, status });
      refreshStats();
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((arr) => arr.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      refreshStats();
      toast.success("Lead deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `vanlinesscout-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const savePrice = async () => {
    if (!selected) return;
    const price = Number(priceInput);
    if (Number.isNaN(price) || price < 0) {
      toast.error("Enter a valid price");
      return;
    }
    try {
      await api.patch(`/admin/leads/${selected.id}/price`, { price });
      setLeads((arr) => arr.map((l) => (l.id === selected.id ? { ...l, price, price_manual: true } : l)));
      setSelected({ ...selected, price, price_manual: true });
      setEditingPrice(false);
      refreshStats();
      toast.success("Price updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const dispatchLead = async (leadId, partnerId = null) => {
    try {
      const res = await api.post(`/admin/leads/${leadId}/dispatch`, null, {
        params: partnerId ? { partner_id: partnerId } : {},
      });
      const ok = (res.data.results || []).every((r) => r.ok);
      if (ok) {
        toast.success(partnerId ? "Lead sent to partner" : "Lead sent to global webhook");
        refreshLeads();
        if (selected?.id === leadId) {
          const fresh = (await api.get("/admin/leads", { params: { q: leadId } })).data.leads.find((l) => l.id === leadId);
          if (fresh) setSelected(fresh);
        }
      } else {
        toast.error("Webhook responded with an error");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-movewave-paper">
        <div className="text-sm uppercase tracking-wider font-bold">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-movewave-paper">
      {/* Top bar */}
      <header className="bg-white border-b-2 border-movewave-ink">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-8 h-8 bg-movewave-blue flex items-center justify-center">
              <Truck className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-xl tracking-tight">
              VanLines <span className="text-movewave-blue">Scout</span>
            </span>
            <span className="ml-2 text-xs uppercase tracking-wider text-gray-400 font-bold">/ admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/partners"
              className="hidden sm:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-700 hover:text-movewave-blue"
              data-testid="nav-partners"
            >
              <Users className="w-3.5 h-3.5" /> Partners
            </Link>
            <span className="hidden md:block text-sm text-gray-600">{user?.email}</span>
            <Button
              variant="outline"
              onClick={logout}
              className="rounded-none border-2 border-movewave-ink h-9 px-3 text-xs font-bold uppercase tracking-wider"
              data-testid="logout-btn"
            >
              <LogOut className="w-3.5 h-3.5 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <StatCard label="Total leads" value={stats?.total ?? 0} icon={<Inbox className="w-4 h-4" />} />
          <StatCard label="New" value={stats?.new ?? 0} icon={<TrendingUp className="w-4 h-4" />} accent />
          <StatCard label="Contacted" value={stats?.contacted ?? 0} icon={<Phone className="w-4 h-4" />} />
          <StatCard label="Sold" value={stats?.sold ?? 0} icon={<CheckCircle2 className="w-4 h-4" />} />
          <StatCard label="Pipeline" value={`$${(stats?.pipeline_value ?? 0).toLocaleString()}`} icon={<DollarSign className="w-4 h-4" />} />
          <StatCard label="Revenue" value={`$${(stats?.revenue ?? 0).toLocaleString()}`} icon={<DollarSign className="w-4 h-4" />} />
        </div>

        {/* Toolbar */}
        <div className="bg-white border-2 border-movewave-ink p-4 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 mb-4">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by name, email, phone, ZIP..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10 rounded-none border-2 border-gray-200 focus:border-movewave-blue focus-visible:ring-0"
                data-testid="leads-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 rounded-none border-2 border-gray-200 h-10" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="sold">Sold</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { refreshLeads(); refreshStats(); }}
              className="rounded-none border-2 border-movewave-ink h-10 px-3 text-xs font-bold uppercase tracking-wider"
              data-testid="refresh-btn"
            >
              <RefreshCcw className="w-3.5 h-3.5 mr-2" /> Refresh
            </Button>
            <Button
              onClick={exportCsv}
              className="rounded-none bg-movewave-orange hover:bg-movewave-orange-hover text-white h-10 px-3 text-xs font-bold uppercase tracking-wider btn-brut"
              data-testid="export-csv-btn"
            >
              <Download className="w-3.5 h-3.5 mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border-2 border-movewave-ink overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-movewave-ink hover:bg-movewave-ink">
                  <TableHead className="text-white font-bold uppercase text-xs tracking-wider">Name</TableHead>
                  <TableHead className="text-white font-bold uppercase text-xs tracking-wider">Contact</TableHead>
                  <TableHead className="text-white font-bold uppercase text-xs tracking-wider">Route</TableHead>
                  <TableHead className="text-white font-bold uppercase text-xs tracking-wider">Size / Type</TableHead>
                  <TableHead className="text-white font-bold uppercase text-xs tracking-wider">Price</TableHead>
                  <TableHead className="text-white font-bold uppercase text-xs tracking-wider">Status</TableHead>
                  <TableHead className="text-white font-bold uppercase text-xs tracking-wider">Received</TableHead>
                  <TableHead className="text-white"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-gray-500 py-16">
                      No leads yet. Submit a quote on the landing page to see it here.
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((l) => (
                  <TableRow
                    key={l.id}
                    className="cursor-pointer hover:bg-movewave-paper"
                    onClick={() => setSelected(l)}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <TableCell className="font-bold">{l.full_name}</TableCell>
                    <TableCell className="text-sm">
                      <div>{l.email}</div>
                      <div className="text-gray-500">{l.phone}</div>
                    </TableCell>
                    <TableCell className="text-sm font-medium">{l.from_zip} → {l.to_zip}</TableCell>
                    <TableCell className="text-sm">
                      <div>{l.home_size}</div>
                      <div className="text-gray-500 capitalize">{(l.move_type || "").replace("_"," ")}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-display font-bold text-base">${Number(l.price || 0).toFixed(0)}</div>
                      {l.price_manual ? (
                        <div className="text-[10px] uppercase tracking-wider text-movewave-orange font-bold">Manual</div>
                      ) : (
                        <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Auto</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_COLORS[l.status] || ""} rounded-none uppercase text-[10px] tracking-wider font-bold`}>
                        {l.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {l.created_at ? new Date(l.created_at).toLocaleString() : ""}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 hover:bg-gray-100" data-testid={`lead-actions-${l.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-none border-2 border-movewave-ink">
                          <DropdownMenuItem onClick={() => updateStatus(l.id, "contacted")}>Mark contacted</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(l.id, "sold")}>Mark sold</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(l.id, "new")}>Mark new</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(l.id, "archived")}>Archive</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => deleteLead(l.id)} className="text-red-600">
                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Lead detail drawer */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg rounded-none border-l-2 border-movewave-ink bg-white">
          <SheetHeader>
            <SheetTitle className="font-display font-black text-2xl tracking-tight">
              {selected?.full_name}
            </SheetTitle>
            <SheetDescription className="text-xs uppercase tracking-wider">
              Lead detail
            </SheetDescription>
          </SheetHeader>
          {selected && (
            <div className="mt-6 space-y-5">
              <DetailRow label="Email" value={
                <a href={`mailto:${selected.email}`} className="text-movewave-blue underline inline-flex items-center gap-1">
                  {selected.email} <ExternalLink className="w-3 h-3" />
                </a>
              } />
              <DetailRow label="Phone" value={
                <a href={`tel:${selected.phone}`} className="text-movewave-blue underline">{selected.phone}</a>
              } />
              <DetailRow label="Route" value={`${selected.from_zip} → ${selected.to_zip}`} />
              <DetailRow label="Move date" value={selected.move_date} />
              <DetailRow label="Move type" value={(selected.move_type || "").replace("_"," ")} />
              <DetailRow label="Home size" value={selected.home_size} />
              <DetailRow label="Extras" value={(selected.extras || []).join(", ") || "—"} />
              <DetailRow label="Submitted" value={new Date(selected.created_at).toLocaleString()} />
              <DetailRow label="Status" value={
                <Badge className={`${STATUS_COLORS[selected.status]} rounded-none uppercase text-[10px] tracking-wider font-bold`}>
                  {selected.status}
                </Badge>
              } />
              <DetailRow label="Price" value={
                editingPrice ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      value={priceInput}
                      onChange={(e) => setPriceInput(e.target.value)}
                      className="h-9 w-28 rounded-none border-2 border-movewave-blue focus-visible:ring-0"
                      data-testid="price-input"
                      autoFocus
                    />
                    <button onClick={savePrice} className="p-1.5 bg-movewave-blue text-white" data-testid="save-price-btn">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditingPrice(false)} className="p-1.5 border-2 border-gray-300">
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="font-display font-black text-2xl">${Number(selected.price || 0).toFixed(0)}</span>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
                      {selected.price_manual ? "Manual" : "Auto-tiered"}
                    </span>
                    <button
                      onClick={() => { setPriceInput(String(selected.price || 0)); setEditingPrice(true); }}
                      className="p-1.5 border-2 border-gray-300 hover:border-movewave-blue"
                      data-testid="edit-price-btn"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )
              } />
              {(selected.sent_to || []).length > 0 && (
                <DetailRow label="Sent to" value={
                  <div className="flex flex-wrap gap-1">
                    {(selected.sent_to || []).map((pid) => {
                      const p = partners.find((x) => x.id === pid);
                      return (
                        <Badge key={pid} className="rounded-none bg-movewave-blue text-white uppercase text-[10px] tracking-wider font-bold">
                          {p?.name || pid}
                        </Badge>
                      );
                    })}
                  </div>
                } />
              )}
              <div className="pt-4 border-t-2 border-gray-100">
                <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">Dispatch</div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => dispatchLead(selected.id)} className="rounded-none bg-movewave-ink text-white font-bold uppercase text-xs tracking-wider" data-testid="dispatch-global-btn">
                    <Globe className="w-3.5 h-3.5 mr-2" /> Send to global webhook
                  </Button>
                  {partners.filter((p) => p.webhook_url).map((p) => (
                    <Button key={p.id} onClick={() => dispatchLead(selected.id, p.id)} variant="outline" className="rounded-none border-2 border-movewave-blue text-movewave-blue font-bold uppercase text-xs tracking-wider" data-testid={`dispatch-partner-${p.id}`}>
                      <Send className="w-3.5 h-3.5 mr-2" /> {p.name} <span className="ml-1 opacity-60">${Number(p.default_price || 0).toFixed(0)}</span>
                    </Button>
                  ))}
                  {partners.length === 0 && (
                    <Link to="/admin/partners" className="text-xs text-movewave-blue underline">+ Add partner</Link>
                  )}
                </div>
              </div>
              <div className="pt-4 border-t-2 border-gray-100 flex flex-wrap gap-2">
                <Button onClick={() => updateStatus(selected.id, "contacted")} className="rounded-none bg-movewave-blue hover:bg-movewave-blue-hover text-white font-bold uppercase text-xs tracking-wider">
                  Mark contacted
                </Button>
                <Button onClick={() => updateStatus(selected.id, "sold")} className="rounded-none bg-green-600 hover:bg-green-700 text-white font-bold uppercase text-xs tracking-wider">
                  Mark sold
                </Button>
                <Button variant="outline" onClick={() => deleteLead(selected.id)} className="rounded-none border-2 border-red-600 text-red-600 font-bold uppercase text-xs tracking-wider">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function StatCard({ label, value, icon, accent = false }) {
  return (
    <div className={`border-2 border-movewave-ink p-4 ${accent ? "bg-movewave-blue text-white" : "bg-white"}`}>
      <div className={`flex items-center gap-2 text-xs uppercase tracking-wider font-bold ${accent ? "text-white/80" : "text-gray-500"}`}>
        {icon} {label}
      </div>
      <div className="font-display font-black text-3xl tracking-tight mt-2">{value}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-1">{label}</div>
      <div className="text-base">{value}</div>
    </div>
  );
}
